import React from 'react';
import { Link } from 'react-router-dom';

interface Feature {
  feature_id: string;
  feature_key: string;
  feature_name: string;
  feature_description: string;
  feature_type: 'quota' | 'boolean' | 'credits';
  reset_cycle: string;
  feature_limit: string | null;
  is_enabled: boolean;
}

interface Plan {
  id: string;
  plan_key: string;
  plan_name: string;
  plan_description: string;
  monthly_price_inr: string;
  academic_session_price_inr: string;
  is_free: boolean;
  is_default: boolean;
  plan_color: string;
  is_popular?: boolean;
  features: Feature[];
}

interface PricingCardProps {
  plan: Plan;
  billingCycle?: 'monthly' | 'academic';
  showFeatures?: boolean;
  variant?: 'default' | 'compact' | 'teaser';
  isCurrentPlan?: boolean;
  isLowerPlan?: boolean;
  onActionClick?: (plan: Plan) => void;
  ctaText?: string;
  ctaLink?: string;
}

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  billingCycle = 'monthly',
  showFeatures = true,
  variant = 'default',
  isCurrentPlan = false,
  isLowerPlan = false,
  onActionClick,
  ctaText,
  ctaLink = '/signup'
}) => {
  
  // Color mapping for Home V2 style - matches PricingTeaser colors
  const getDisplayColor = (planKey: string): string => {
    const colorMap: { [key: string]: string } = {
      'free': '#2d9cdb',
      'basic': '#28a745',
      'premium': '#f39c12',
      'pro': '#667eea'
    };
    return colorMap[planKey.toLowerCase()] || plan.plan_color;
  };

  const displayColor = getDisplayColor(plan.plan_key);

  const getFeatureIcon = (featureKey: string): string => {
    const icons: { [key: string]: string } = {
      custom_quiz: 'fa-edit',
      quiz_solutions: 'fa-lightbulb',
      report_card: 'fa-chart-bar',
      performance_analysis: 'fa-brain',
      pyqs: 'fa-history',
      diagnostic_test: 'fa-stethoscope',
      study_plan: 'fa-calendar-check',
      ai_mentor: 'fa-robot',
      mock_tests: 'fa-clipboard-check',
      score_predictor: 'fa-chart-line'
    };
    return icons[featureKey] || 'fa-check';
  };

  const getFeatureDisplay = (feature: Feature): string => {
    if (!feature.is_enabled) return '';
    
    if (feature.feature_type === 'boolean') {
      return feature.feature_name;
    }
    
    if (feature.feature_limit === null) {
      return `${feature.feature_name} - Unlimited`;
    }
    
    if (feature.feature_type === 'quota') {
      return `${feature.feature_limit} ${feature.feature_name}`;
    }
    
    if (feature.feature_type === 'credits') {
      return `${feature.feature_limit} ${feature.feature_name} Credits`;
    }
    
    return feature.feature_name;
  };

  const formatPrice = (price: string): string => {
    const numPrice = parseFloat(price);
    if (numPrice === 0) return '₹0';
    return `₹${numPrice.toLocaleString('en-IN')}`;
  };

  const calculateSavingsPercentage = (): number => {
    if (plan.is_free) return 0;
    
    const monthlyPrice = parseFloat(plan.monthly_price_inr);
    const academicPrice = parseFloat(plan.academic_session_price_inr);
    const monthlyYearlyTotal = monthlyPrice * 12;
    
    if (monthlyYearlyTotal === 0) return 0;
    
    const savings = monthlyYearlyTotal - academicPrice;
    const savingsPercentage = (savings / monthlyYearlyTotal) * 100;
    
    return Math.round(savingsPercentage);
  };

  const getPlanIcon = (planKey: string): string => {
    const icons: { [key: string]: string } = {
      'free': 'fas fa-gift',
      'basic': 'fas fa-star',
      'premium': 'fas fa-crown',
      'pro': 'fas fa-gem'
    };
    return icons[planKey.toLowerCase()] || 'fas fa-star';
  };

  const getCTAText = (): string => {
    if (ctaText) return ctaText;
    if (isCurrentPlan) return 'Current Plan';
    if (isLowerPlan) return plan.is_free ? 'Get Started' : 'Upgrade Now';
    return plan.is_free ? 'Start Free' : 'Get Started';
  };

  const price = billingCycle === 'monthly' ? plan.monthly_price_inr : plan.academic_session_price_inr;
  const period = billingCycle === 'monthly' ? 'per month' : 'academic session';
  const isPlanDisabled = isCurrentPlan || isLowerPlan;

  return (
    <div 
      className={`card h-100 border-0 shadow-lg position-relative ${plan.is_popular ? 'border-3' : ''}`}
      style={{
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        borderColor: plan.is_popular ? displayColor : 'transparent',
        transform: plan.is_popular ? 'scale(1.05)' : 'scale(1)',
        opacity: isPlanDisabled ? 0.8 : 1
      }}
      onMouseEnter={(e) => {
        if (!isPlanDisabled) {
          e.currentTarget.style.transform = plan.is_popular ? 'scale(1.08)' : 'scale(1.03)';
          e.currentTarget.style.boxShadow = '0 25px 50px rgba(0,0,0,0.2)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = plan.is_popular ? 'scale(1.05)' : 'scale(1)';
        e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
      }}
    >
      {/* Popular Badge */}
      {plan.is_popular && !isCurrentPlan && (
        <div className="position-absolute top-0 start-50 translate-middle">
          <span 
            className="badge rounded-pill px-4 py-2"
            style={{
              background: displayColor,
              color: 'white',
              fontSize: '0.85rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}
          >
            <i className="fas fa-fire me-1"></i>
            MOST POPULAR
          </span>
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="position-absolute top-0 start-50 translate-middle">
          <span 
            className="badge rounded-pill px-4 py-2"
            style={{
              background: displayColor,
              color: 'white',
              fontSize: '0.85rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}
          >
            <i className="fas fa-check-circle me-1"></i>
            CURRENT PLAN
          </span>
        </div>
      )}

      <div className="card-body p-4 text-center">
        {/* Icon */}
        <div 
          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
          style={{
            width: '80px',
            height: '80px',
            background: `${displayColor}15`,
            border: `3px solid ${displayColor}`
          }}
        >
          <i className={getPlanIcon(plan.plan_key)} style={{ color: displayColor, fontSize: '2rem' }}></i>
        </div>

        {/* Plan Name */}
        <h3 className="fw-bold mb-2" style={{ color: displayColor }}>
          {plan.plan_name}
        </h3>

        {/* Description */}
        {variant !== 'compact' && plan.plan_description && (
          <p className="text-muted small mb-3">{plan.plan_description}</p>
        )}

        {/* Price */}
        <div className="mb-4">
          <span className="display-4 fw-bold" style={{ color: displayColor }}>
            {formatPrice(price)}
          </span>
          {!plan.is_free && (
            <span className="text-muted d-block">{period}</span>
          )}
          {plan.is_free && (
            <span className="text-muted d-block">Forever</span>
          )}
        </div>

        {/* Savings Badge for Academic Session */}
        {billingCycle === 'academic' && !plan.is_free && calculateSavingsPercentage() > 0 && (
          <div className="mb-3">
            <span className="badge bg-success">
              <i className="fas fa-tags me-1"></i>
              Save {calculateSavingsPercentage()}%
            </span>
          </div>
        )}

        {/* Features List */}
        {showFeatures && (
          <ul className="list-unstyled text-start mb-4">
            {plan.features
              .filter(f => f.is_enabled)
              .slice(0, variant === 'compact' ? 5 : 20)
              .map((feature) => (
                <li key={feature.feature_id} className="d-flex align-items-start mb-3">
                  <i 
                    className={`fas ${getFeatureIcon(feature.feature_key)} me-2 mt-1`}
                    style={{ color: displayColor }}
                  ></i>
                  <span className="text-muted small">{getFeatureDisplay(feature)}</span>
                </li>
              ))}
          </ul>
        )}

        {/* CTA Button */}
        {onActionClick ? (
          <button
            className="btn btn-lg w-100 rounded-pill"
            style={{
              background: plan.is_popular || isCurrentPlan ? displayColor : 'transparent',
              border: `2px solid ${displayColor}`,
              color: plan.is_popular || isCurrentPlan ? 'white' : displayColor,
              fontWeight: '600',
              transition: 'all 0.3s ease',
              cursor: isPlanDisabled ? 'not-allowed' : 'pointer'
            }}
            onClick={() => !isPlanDisabled && onActionClick(plan)}
            disabled={isPlanDisabled}
            onMouseEnter={(e) => {
              if (!isPlanDisabled) {
                e.currentTarget.style.background = displayColor;
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 8px 16px ${displayColor}40`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = plan.is_popular || isCurrentPlan ? displayColor : 'transparent';
              e.currentTarget.style.color = plan.is_popular || isCurrentPlan ? 'white' : displayColor;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {getCTAText()} <i className="fas fa-arrow-right ms-2"></i>
          </button>
        ) : (
          <Link 
            to={ctaLink}
            className="btn btn-lg w-100 rounded-pill"
            style={{
              background: plan.is_popular ? displayColor : 'transparent',
              border: `2px solid ${displayColor}`,
              color: plan.is_popular ? 'white' : displayColor,
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = displayColor;
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 8px 16px ${displayColor}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = plan.is_popular ? displayColor : 'transparent';
              e.currentTarget.style.color = plan.is_popular ? 'white' : displayColor;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {getCTAText()} <i className="fas fa-arrow-right ms-2"></i>
          </Link>
        )}
      </div>
    </div>
  );
};

export default PricingCard;
